import { T, Translate } from '../../i18n/index';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { Icon } from '../icon';
import { MaterialIcon } from '../icon/icons';
import { addStyles, DefaultTheme } from '../theme';
import { PHOTO_MEASURE_SVG_URL, PHOTO_MEASURE_SVG_URL_DISABLED } from './photomeasure.svg';

export const CSS = {
	container: 'c-FieldActionBtn',
	isLinkable: 'c-FieldActionBtn--linkable',
	isLinking: 'c-FieldActionBtn--linking',
	joinable: 'c-FieldActionBtn--joinable',
	linkContainer: 'c-FieldActionBtn__Link',
	photoMeasureIcon: 'c-FieldActionBtn__PhotoMeasure',
	errorIcon: 'c-FieldActionBtn__error',
};

const Style = {
	[CSS.container]: {
		display: 'flex',
		'justify-content': 'flex-end',
		width: '100%',
	},
	[CSS.isLinkable + ' button']: {
		color: DefaultTheme.font.color.white,
		'background-color': DefaultTheme.color.accent,
	},
	[CSS.joinable]: {
		'background-image': PHOTO_MEASURE_SVG_URL,
		'background-repeat': 'no-repeat',
		'background-size': '18px',
		width: '20px',
		height: '20px',
		'background-position': 'center',
		'&[disabled]': {
			'background-image': PHOTO_MEASURE_SVG_URL_DISABLED,
		},
	},
	[CSS.linkContainer]: {
		'font-size': '12px',
		'background-color': DefaultTheme.font.color.supporting,
		border: 'none',
		outline: 'none',
		color: DefaultTheme.font.color.white,
		padding: '0 ' + DefaultTheme.space(2),
		'align-items': 'center',
		'border-radius': DefaultTheme.space(4),
		'font-weight': 'bold',
		display: 'flex',
		cursor: 'pointer',
		'& i': {
			'font-size': '20px',
			'margin-left': DefaultTheme.space(1),
		},
		width: '100%',
		'justify-content': 'center',
		'&:disabled': {
			opacity: '0.5',
			cursor: 'not-allowed',
		},
	},
	[CSS.photoMeasureIcon]: {
		transform: ' rotate(-90deg) !important',
	},
	[CSS.errorIcon]: {
		color: DefaultTheme.font.color.error,
		cursor: 'default',
	},
};

addStyles(Style);

export interface FieldActionButtonCfg {
	icon: MaterialIcon;
	text: T;
	linkingText?: T;
	title?: T;
	linking?: boolean;
	disabled?: boolean;
	onclick?: (e: Event) => any;
}

export interface LinkHeightButtonCfg {
	linking?: boolean;
	joinable?: boolean;
	onclick?: (e: Event) => any;
}

export interface LinkVerticalSeparationButtonCfg {
	linking?: boolean;
	joinable?: boolean;
	hasTop?: boolean;
	hasBottom?: boolean;
	hasError?: boolean;
	onclick?: (e: Event) => any;
}

export const FieldActionButton = {
	view(cfg: FieldActionButtonCfg): MithrilVNode {
		const icon = cfg.linking ? MaterialIcon.CLOSE : cfg.icon;

		const text = cfg.linking ? cfg.linkingText : cfg.text;
		const title = cfg.title;

		return m('div', { className: `${CSS.container} ${cfg.linking ? CSS.isLinking : CSS.isLinkable}` }, [
			m(
				'button',
				{
					className: CSS.linkContainer,
					onclick: cfg.onclick,
					title: Translate(title),
					disabled: cfg.disabled,
				},
				[Translate(text), Icon.view(icon, { type: 'button' })]
			),
		]);
	},

	viewLinkHeight(cfg: LinkHeightButtonCfg): MithrilVNode {
		let icon: MithrilVNode;

		if (cfg.joinable) {
			icon = Icon.view(MaterialIcon.NONE, {
				type: 'button',
				className: `${CSS.photoMeasureIcon} ${CSS.joinable}`,
			});
		} else if (cfg.linking) {
			icon = Icon.view(MaterialIcon.CLOSE, { type: 'button' });
		} else {
			icon = Icon.view(MaterialIcon.PHOTO_MEASURE, { type: 'button', className: CSS.photoMeasureIcon });
		}

		const text = cfg.linking ? T.PHOTOMEASURE_LINK : T.PHOTOMEASURE_LINK_START;
		const title = cfg.joinable ? T.PHOTOMEASURE_JOIN : T.PHOTOMEASURE_LINK_START;
		return m('div', { className: `${CSS.container} ${cfg.linking ? CSS.isLinking : CSS.isLinkable}` }, [
			m('button', { className: CSS.linkContainer, onclick: cfg.onclick, title: Translate(title) }, [
				Translate(text),
				icon,
			]),
		]);
	},

	viewLinkVerticalSeparation(cfg: LinkVerticalSeparationButtonCfg): MithrilVNode {
		let icon: MithrilVNode;
		let text: string;
		let className = `${CSS.container} ${cfg.linking ? CSS.isLinking : CSS.isLinkable}`;
		let title: string;

		var errorIcon = Icon.view(MaterialIcon.WARNING, { type: 'button', className: `${CSS.errorIcon}` });
		if (cfg.joinable) {
			icon = Icon.view(MaterialIcon.NONE, {
				type: 'button',
				className: `${CSS.photoMeasureIcon} ${CSS.joinable}`,
			});
			text = title = Translate(T.VERTICAL_SEPARATION_TOP);
		} else if (cfg.linking) {
			icon = Icon.view(MaterialIcon.CLOSE, { type: 'button' });
			text = title = Translate(T.PHOTOMEASURE_LINK);
		} else {
			icon = Icon.view(MaterialIcon.PHOTO_MEASURE, { type: 'button', className: CSS.photoMeasureIcon });
			text = title = Translate(T.VERTICAL_SEPARATION_TOP);
			if (cfg.hasTop && !cfg.hasBottom) {
				text = title = Translate(T.VERTICAL_SEPARATION_BOTTOM);
			}
		}

		var resultDiv = [];

		if (cfg.hasError) {
			resultDiv.push(errorIcon);
			title = Translate(T.VERTICAL_SEPARATION_INVALID);
		}

		resultDiv.push(m('button', { className: CSS.linkContainer, onclick: cfg.onclick }, [text, icon]));

		return m('div', { className, title }, resultDiv);
	},
};
